import {useCallback, useEffect, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {logService} from '@/services/logService';
import type {AfriexBusinessLog, AfriexLogParams} from '@/types';

const useLogList = (tags: string[] = []) => {
  const [logsList, setLogsList] = useState<AfriexBusinessLog[]>([]);
  const [logsTotal, setLogsTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch] = useState<boolean>(false);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  // Whatever logService rejects with; the hook only exposes it, never reads it.
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexLogParams>({
    tags,
  });
  const [filtersToApply, setFiltersToApply] = useState<AfriexLogParams>({
    tags,
  });

  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchAllLogs = useCallback(
    async (params: AfriexLogParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data, total} = await logService.getLogList({
          ...params,
          ...currentFilters,
        });
        setLogsList(data);
        setLogsTotal(total ?? 0);
        if (total && total > 10) {
          setShowPagination(true);
        }
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  const tagsKey = tags.join(',');
  useEffect(() => {
    // Fire-and-forget: fetchAllLogs catches its own failures into `error`, so
    // this only stops an unexpected throw becoming an unhandled rejection.
    fetchAllLogs({tags}).catch(() => {});
  }, [fetchAllLogs, tagsKey]);

  const handleApplyFilters = useCallback(() => {
    const applied = applyFilters(filtersToApply);
    // Pagination reset is success-only work. applyFilters returns false when it
    // rejects a date range, and Pagination reacts to this flag by calling
    // onPageChange -> a refetch, so toggling it unconditionally fired a request
    // the user never asked for. See hooks/useFilters.ts.
    if (!applied) {
      return false;
    }

    setIsPageReset((prev) => !prev);
    return true;
  }, [applyFilters, filtersToApply]);

  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count dynamic filters with a real value (excluding base tags
        // scope), matching useFilters.applyFilters' own falsy/empty-string
        // stripping so the badge count never overstates what actually
        // reaches the API.
        const filteredCount = Object.entries(updatedFilters).filter(
          ([key, val]) => key !== 'tags' && Boolean(val),
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({tags});
    setFiltersToApply({tags});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };
  return {
    error,
    fetchAllLogs,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: logsTotal,
    },
    showPagination,
    logsList,
    logsTotal,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
  };
};

export default useLogList;
